import { useEffect, useMemo } from 'react';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../AuthMachine/hooks';
import { useChallengesQuery } from '../GraphQL/Challenges.generated';
import { useHasUnseenChallengesQuery } from '../GraphQL/HasUnseenChallenges.generated';

export const useChallenges = () => {
  const isLoggedIn = useIsLoggedIn();
  const [challengesResponse, fetchChallenges] = useChallengesQuery({
    requestPolicy: useChallengesNetworkPolicy(),
    pause: true,
  });
  const [hasUnseenChallengesResponse, fetchHasUnseenChallenges] =
    useHasUnseenChallengesQuery({
      requestPolicy: 'network-only',
      pause: true,
    });

  // fetch has unseen challenges state at mount and challenges change
  useEffect(() => {
    if (isLoggedIn) fetchHasUnseenChallenges();
  }, [challengesResponse.data, fetchHasUnseenChallenges, isLoggedIn]);

  const hasUnseenChallenges =
    hasUnseenChallengesResponse.data?.hasUnseenChallenges
      ?.hasUnseenChallenges ?? false;

  const challenges = challengesResponse.data?.challenges;
  const isFetchingChallenges = challengesResponse.fetching;
  const challengesError = challengesResponse.error;

  return useMemo(
    () => ({
      hasUnseenChallenges,
      challenges,
      isFetchingChallenges,
      challengesError,
      fetchChallenges,
      fetchHasUnseenChallenges,
    }),
    [
      challenges,
      challengesError,
      hasUnseenChallenges,
      isFetchingChallenges,
      fetchChallenges,
      fetchHasUnseenChallenges,
    ],
  );
};

const useChallengesNetworkPolicy = () => {
  const shouldUseCache = useFeatureFlag(
    'XP-3188-short-challenges-cache-enabled',
  );

  return shouldUseCache ? 'cache-and-network' : 'network-only';
};
